import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileBarChart, Lock, Bookmark } from "lucide-react";
import { api, formatApiError, trackStage, trackEvent, getSid } from "../api";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, DemoRibbon } from "../components/StatusBadge";
import LoginPrompt from "../components/LoginPrompt";
import { Button } from "../components/ui/button";

export default function ReportPreviewPage() {
  const { parcelId } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);
  const [prompt, setPrompt] = useState(false);
  const [savePrompt, setSavePrompt] = useState(false);
  const [saved, setSaved] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [locked, setLocked] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    const realId = parcelId.replaceAll("~", "/");
    const req = realId.includes("/")
      ? api.get("/reports/preview-real", { params: { id: realId }, timeout: 75000 })
      : api.get(`/reports/preview/${parcelId}`, { timeout: 75000 });
    // report_preview analytics is backend-side only, after a successful
    // renderable response — a click or a failed/timed-out fetch never counts.
    req.then((r) => {
      if (!r?.data?.parcel_identification) { setError("generic"); return; }
      setData(r.data);
      trackStage("funnel_preview");
      trackEvent("save_parcel_cta_view");
    }).catch((e) => {
      const status = e?.response?.status;
      setError(status === 404 ? "notfound" : status === 503 ? "source" : "generic");
    });
  }, [parcelId, retryNonce]);

  // Save intent survives the auth round-trip: count the post-registration return once.
  useEffect(() => {
    try {
      const realId = parcelId.replaceAll("~", "/");
      if (user && sessionStorage.getItem("pg_pending_save") === realId) {
        sessionStorage.removeItem("pg_pending_save");
        sessionStorage.setItem("pg_post_reg", realId);
        trackEvent("save_parcel_after_registration");
      }
    } catch { /* private mode */ }
  }, [user, parcelId]);

  if (error) return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <div data-testid="preview-error-box" role="alert" className="rounded-xl border border-rose-300 bg-rose-50 p-6">
        <p data-testid="preview-error-text" className="text-sm text-rose-800">
          {error === "notfound" ? t("reports.error.notfound") : error === "source" ? t("reports.error.source") : t("reports.error.generic")}
        </p>
        {error !== "notfound" && (
          <Button data-testid="preview-error-retry" variant="outline" size="sm" className="mt-3"
                  onClick={() => setRetryNonce((n) => n + 1)}>
            {t("reports.error.retry")}
          </Button>
        )}
      </div>
    </div>
  );
  if (data === null) return <div data-testid="preview-loading" className="p-20 text-center text-slate-500">{t("common.loading")}</div>;

  const pid = data.parcel_identification;
  const ps = data.planning_status;

  const fullReport = async () => {
    if (!user) { setPrompt(true); return; }
    setGenerating(true);
    try {
      const realId = parcelId.replaceAll("~", "/");
      const { data: r } = await api.post("/reports", { parcel_id: realId }, { timeout: 90000 });
      navigate(`/raport/${r.id}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402 && d?.code === "report_locked") {
        setLocked(d);
        trackEvent("upgrade_prompt_view", { ref: "report_locked" });
      } else {
        toast.error(formatApiError(e));
      }
      setGenerating(false);
    }
  };

  const saveParcel = async () => {
    trackEvent("save_parcel_cta_click");
    const realId = parcelId.replaceAll("~", "/");
    if (!user) {
      try { sessionStorage.setItem("pg_pending_save", realId); } catch { /* private mode */ }
      setSavePrompt(true);
      return;
    }
    try {
      await api.post("/saved-parcels", { parcel_id: realId, sid: getSid() });
      try {
        localStorage.setItem("pg_ret_s", "1");
        localStorage.setItem("pg_ret_w", "1");
        if (sessionStorage.getItem("pg_post_reg") === realId) {
          sessionStorage.removeItem("pg_post_reg");
          trackEvent("save_after_reg_success");
        }
      } catch { /* private mode */ }
      setSaved(true);
      toast.success(t("conv.saved_confirm"));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const statusCards = [
    { label: t("parcel.planOgolny"), status: ps.plan_ogolny_status, text: t(`status.${ps.plan_ogolny_status}`), testId: "preview-status-plan-ogolny" },
    { label: t("parcel.mpzp"), status: ps.mpzp_status, text: t(`mpzp.${ps.mpzp_status}`), testId: "preview-status-mpzp" },
    ...(data.data_kind === "official" ? [] : [
      { label: t("parcel.proceedings"), status: ps.proceedings_status, text: t(`proc.${ps.proceedings_status}`), testId: "preview-status-proceedings" },
      { label: t("parcel.consultation"), status: ps.consultation_status, text: t(`cons.${ps.consultation_status}`), testId: "preview-status-consultation" },
    ]),
  ];

  return (
    <div data-testid="report-preview-page" className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <article className="rounded-xl border bg-white p-8 shadow-sm sm:p-12">
        <header className="border-b-2 border-slate-900 pb-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-heading text-xs font-bold uppercase tracking-widest text-[#DC143C]">PlanGmina</p>
              <h1 className="mt-2 break-all font-heading text-2xl font-extrabold tracking-tight sm:text-3xl">
                {t("report.title")} — {pid.number}
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                {[pid.address, pid.gmina && `${t("common.gmina")} ${pid.gmina}`, pid.powiat && `${t("common.powiat")} ${pid.powiat}`, pid.wojewodztwo && `${t("common.woj")} ${pid.wojewodztwo}`].filter(Boolean).join(" · ")}
              </p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
              <span data-testid="preview-badge" className="rounded border border-amber-400 bg-amber-50 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-700">
                {t("preview.badge")}
              </span>
              {data.data_kind === "official" ? (
                <span data-testid="official-data-badge" className="rounded border border-emerald-400 bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                  {t("data.official")}
                </span>
              ) : data.is_demo && <DemoRibbon label={t("demo.label")} />}
            </div>
          </div>
        </header>

        <section className="mt-8">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("report.status")}</h2>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {statusCards.map((c) => (
              <div key={c.testId} data-testid={c.testId} className="rounded-lg border p-3">
                <p className="text-[10px] uppercase tracking-wide text-slate-400">{c.label}</p>
                <div className="mt-1.5"><StatusBadge status={c.status} label={c.text} /></div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-slate-700"><span className="font-semibold">{t("parcel.zone")}:</span> {ps.planning_zone || t("data.none")}</p>
          {ps.mpzp_name && <p className="mt-1 text-xs text-slate-500">{ps.mpzp_name}</p>}
        </section>

        {data.flood && (
          <section data-testid="preview-flood-section" className="mt-8">
            <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("flood.title")}</h2>
            <div className="mt-3">
              <StatusBadge
                status={data.flood.status}
                label={lang === "pl" ? data.flood.badge_pl : data.flood.badge_en}
                testId="preview-flood-badge"
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">{t("flood.locked.note")}</p>
          </section>
        )}

        {data.planning_registry && (
          <section data-testid="preview-ru-section" className="mt-8">
            <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("ru.title")}</h2>
            <p className="mt-3 text-sm font-semibold" data-testid="preview-ru-label">{lang === "pl" ? data.planning_registry.label_pl : data.planning_registry.label_en}</p>
            <p className="mt-2 text-xs text-slate-500">{t("ru.locked.note")}</p>
          </section>
        )}

        {data.heritage && (
          <section data-testid="preview-heritage-section" className="mt-8">
            <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("heritage.title")}</h2>
            <div className="mt-3">
              <StatusBadge
                status={data.heritage.status}
                label={lang === "pl" ? data.heritage.badge_pl : data.heritage.badge_en}
                testId="preview-heritage-badge"
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">{t("heritage.locked.note")}</p>
          </section>
        )}

        {data.elevation && (
          <section data-testid="preview-elevation-section" className="mt-8">
            <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("elevation.title")}</h2>
            <div className="mt-3">
              <StatusBadge
                status={data.elevation.status === "DETECTED" ? "unknown" : data.elevation.status}
                label={lang === "pl" ? data.elevation.badge_pl : data.elevation.badge_en}
                testId="preview-elevation-badge"
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">{t("elevation.locked.note")}</p>
          </section>
        )}

        {data.soils && (
          <section data-testid="preview-soils-section" className="mt-8">
            <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("soils.title")}</h2>
            <div className="mt-3">
              <StatusBadge
                status={data.soils.status === "DETECTED" ? "unknown" : data.soils.status}
                label={lang === "pl" ? data.soils.badge_pl : data.soils.badge_en}
                testId="preview-soils-badge"
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">{t("soils.locked.note")}</p>
          </section>
        )}

        <section className="mt-8">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("evidence.facts")}</h2>
          <table data-testid="preview-facts-table" className="mt-3 w-full table-fixed border-collapse text-xs">
            <thead>
              <tr className="bg-slate-900 text-left text-white">
                <th className="px-3 py-2">Fakt</th>
                <th className="px-3 py-2">Wartość</th>
                <th className="px-3 py-2">{t("report.source")}</th>
                <th className="px-3 py-2">{t("report.verification")}</th>
              </tr>
            </thead>
            <tbody>
              {data.evidence_table.map((e, i) => (
                <tr key={i} data-testid={`preview-fact-row-${i}`} className="border-b align-top">
                  <td className="px-3 py-2 font-medium break-words">{lang === "pl" ? e.fact_pl : e.fact_en}</td>
                  <td className="px-3 py-2 break-words">{lang === "pl" ? e.value_pl : e.value_en}</td>
                  <td className="px-3 py-2 text-slate-600 break-words">{e.source_authority}</td>
                  <td className="px-3 py-2 break-words">{e.verification_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {data.important_dates.length > 0 && (
          <section className="mt-8">
            <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("report.dates")}</h2>
            <ul className="mt-3 space-y-1.5 text-sm">
              {data.important_dates.map((x, i) => (
                <li key={i} className="flex gap-3">
                  <span className="font-mono-data text-xs text-slate-500">{x.date}</span>
                  <span>{lang === "pl" ? x.label_pl : x.label_en}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-8">
          <h2 className="font-heading text-sm font-bold uppercase tracking-widest text-slate-500">{t("report.documents")}</h2>
          {data.documents.length === 0 && (
            <p data-testid="preview-documents-none" className="mt-3 text-sm text-slate-500">{t("data.none")}</p>
          )}
          <ul className="mt-3 space-y-2 text-sm">
            {data.documents.map((doc) => (
              <li key={doc.id} className="rounded-lg border px-4 py-2.5">
                <p className="font-medium">{doc.title}</p>
                <p className="text-xs text-slate-500">{doc.authority} · {doc.publication_date} · {doc.status}</p>
              </li>
            ))}
          </ul>
        </section>

        <section data-testid="preview-cta-box" className="mt-10 rounded-xl border-2 border-dashed border-[#DC143C]/40 bg-[#FEF2F2]/40 p-6">
          <div className="flex items-start gap-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#DC143C] text-white">
              <Lock size={18} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm leading-relaxed text-slate-700">{t("preview.note")}</p>
              <Button
                data-testid="preview-full-report-button"
                className="mt-4 h-auto whitespace-normal bg-[#DC143C] text-left text-white hover:bg-[#B91C1C]"
                onClick={fullReport}
                disabled={generating}
              >
                <FileBarChart size={15} className="mr-2" />
                {generating ? t("common.loading") : user ? t("preview.generate") : t("preview.cta")}
              </Button>
            </div>
          </div>
        </section>

        {locked && (
          <section data-testid="report-upgrade-card" className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-6">
            <h2 className="font-heading text-base font-bold text-slate-900">{t("report.upgrade.title")}</h2>
            <p className="mt-1 text-sm text-slate-600">{t("report.upgrade.text")}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                data-testid="unlock-report-39"
                className="bg-[#DC143C] text-white hover:bg-[#B91C1C]"
                onClick={() => { trackEvent("upgrade_prompt_click", { ref: "report_locked" }); navigate(`/platnosc/raport/${parcelId}?returnTo=${encodeURIComponent(`/raport/podglad/${parcelId}`)}`); }}
              >
                {t("report.upgrade.cta")}
              </Button>
              <Button
                data-testid="unlock-report-pro"
                variant="outline"
                onClick={() => { trackEvent("upgrade_prompt_click", { ref: "report_pro" }); navigate("/platnosc/plan/pro"); }}
              >
                {t("report.upgrade.cta_pro")}
              </Button>
            </div>
          </section>
        )}

        <section data-testid="track-parcel-cta" className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-6">
          <h2 className="font-heading text-base font-bold text-slate-900">{saved ? t("conv.saved_confirm") : t("conv.title")}</h2>
          <p className="mt-1 text-sm leading-relaxed text-slate-600">{saved ? t("watch.active_text") : t("conv.text")}</p>
          <Button data-testid="save-parcel-cta-button" variant="outline" className="mt-3" onClick={saveParcel} disabled={saved}>
            <Bookmark size={15} className="mr-2" />
            {saved ? t("parcel.saved") : t("conv.save")}
          </Button>
        </section>

        <footer className="mt-10 border-t pt-5">
          <p data-testid="preview-disclaimer" className="text-xs leading-relaxed text-slate-500">
            {lang === "pl" ? data.disclaimer_pl : data.disclaimer_en}
          </p>
        </footer>
      </article>

      <LoginPrompt open={prompt} onOpenChange={setPrompt} message={t("prompt.report")} />
      <LoginPrompt open={savePrompt} onOpenChange={setSavePrompt} message={t("prompt.save")} />
    </div>
  );
}
